import React, { useState, useEffect } from "react";
import { View, Text, ActivityIndicator, StyleSheet, Modal } from "react-native";
import { Auth } from "aws-amplify";
import QRCode from "qrcode";
import MainStepBox from "./MfaSetup/MainStepBox";
import { Button } from "./MfaSetup/Button";
import { Colors } from "@constants";
import { I18n } from "react-redux-i18n";
import * as Sentry from "@sentry/react";

enum MfaStatus {
  Disabled = "NOMFA",
  Enabled = "TOTP",
}

export default function MfaSetup() {
  const [user, setUser] = useState<any>(null);
  const [qrUri, setQrUri] = useState<string>("");
  const [code, setCode] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [verified, setVerified] = useState<boolean>(false);
  const [mfaStatus, setMfaStatus] = useState<string>("");
  const [isCheckingStatus, setIsCheckingStatus] = useState<boolean>(true);
  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);

  useEffect(() => {
    checkMfaStatus();
  }, []);

  const [uiStep, setUiStep] = useState(1);

  const checkMfaStatus = async () => {
    setIsCheckingStatus(true);
    try {
      const freshUser = await Auth.currentAuthenticatedUser({
        bypassCache: true,
      });
      setUser(freshUser);
      const pref = await Auth.getPreferredMFA(freshUser);
      setMfaStatus(pref);
    } catch (err: any) {
      Sentry.captureException(err, {
        extra: {
          module: "MfaSetup",
          method: "checkMfaStatus",
        },
        tags: {
          cognito: "error",
        },
      });
      setError("Error checking MFA status.");
    }
    setIsCheckingStatus(false);
  };

  const handleSetup = async () => {
    setLoading(true);
    setError("");
    try {
      const freshUser = await Auth.currentAuthenticatedUser({
        bypassCache: true,
      });
      setUser(freshUser);
      await Auth.currentSession();

      const pref = await Auth.getPreferredMFA(freshUser);
      if (pref === MfaStatus.Disabled) {
        const totpSecret = await Auth.setupTOTP(freshUser);

        const username = freshUser.getUsername();
        const label = encodeURIComponent(`foodi:${username}`);
        const issuer = encodeURIComponent("foodi");
        const otpAuth = `otpauth://totp/${label}?secret=${totpSecret}&issuer=${issuer}`;

        const dataUrl = await QRCode.toDataURL(otpAuth);
        setQrUri(dataUrl);
        setUiStep(2);
      } else {
        setError(I18n.t("profile.mfa.alreadyEnabled"));
      }
    } catch (err: any) {
      Sentry.captureException(err, {
            extra:{
              module: "MfaSetup",
              method: "handleSetup",
            },
            tags: {
              cognito: "error",
            },
          });
      setError(I18n.t("profile.mfa.setupFailed"));
    }
    setLoading(false);
  };

  const handleVerify = async () => {
    if (!user) return;
    setLoading(true);
    setError("");
    try {
      const freshUser = await Auth.currentAuthenticatedUser({
        bypassCache: true,
      });
      await Auth.verifyTotpToken(freshUser, code.trim());
      await Auth.setPreferredMFA(freshUser, MfaStatus.Enabled);
      setVerified(true);
      setMfaStatus(MfaStatus.Enabled);
      setUiStep(3);
    } catch (err: any) {
      setError(I18n.t("profile.mfa.activate.step3.invalidCode"));
    }
    setLoading(false);
  };

  const handleDisableMfa = () => {
    if (!user) return;
    setShowConfirmModal(true);
  };

  const confirmDisableMfa = async () => {
    setShowConfirmModal(false);
    setLoading(true);
    setError("");
    try {
      const freshUser = await Auth.currentAuthenticatedUser({
        bypassCache: true,
      });
      await Auth.setPreferredMFA(freshUser, MfaStatus.Disabled);
      setMfaStatus(MfaStatus.Disabled);
      setVerified(false);
      setQrUri("");
      setCode("");
    } catch (err: any) {
      Sentry.captureException(err, {
        extra: {
          module: "MfaSetup",
          method: "confirmDisableMfa",
        },
        tags: {
          cognito: "error",
        },
      });
      setError(I18n.t("profile.mfa.disableConfirmation.disableFailed"));
    }
    resetSetup();
    setLoading(false);
  };

  const resetSetup = () => {
    setQrUri("");
    setCode("");
    setError("");
    setVerified(false);
    setUiStep(1);
  };

  if (isCheckingStatus) {
    return (
      <View style={styles.container}>
        <ActivityIndicator style={styles.loader} />
        <Text>{I18n.t("profile.mfa.checking")}</Text>
      </View>
    );
  }

  return (
    <>
      <View style={styles.container}>
        <Text style={styles.statusText}>
          {I18n.t("profile.mfa.status") + ": "}
          {mfaStatus === MfaStatus.Disabled
            ? I18n.t("profile.mfa.statusDisabled")
            : I18n.t("profile.mfa.statusEnabled")}
        </Text>

        {mfaStatus !== MfaStatus.Disabled && (
          <View style={styles.buttonContainer}>
            <Button type="secondary" onPress={handleDisableMfa}>
              <Text>{I18n.t("profile.mfa.disable")}</Text>
            </Button>
          </View>
        )}
      </View>

      {mfaStatus === MfaStatus.Disabled && (
        <MainStepBox
          step={uiStep}
          onStart={handleSetup}
          onCancel={resetSetup}
          onPrevStep={() => setUiStep(uiStep - 1)}
          onVerify={uiStep === 2 ? () => setUiStep(3) : handleVerify}
          onCodeChange={setCode}
          code={code}
          qrUri={qrUri}
          loading={loading}
          error={error}
          verified={verified}
        />
      )}

      <Modal
        visible={showConfirmModal}
        transparent={true}
        animationType="fade"
        onRequestClose={() => setShowConfirmModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContainer}>
            <Text style={styles.modalTitle}>Disable MFA</Text>
            <Text style={styles.modalMessage}>
              {I18n.t("profile.mfa.disableConfirmation.title")}
            </Text>
            <View style={styles.modalButtons}>
              <Button
                type="primary"
                onPress={() => setShowConfirmModal(false)}
                disabled={loading}
              >
                <Text>{I18n.t("profile.mfa.cancel")}</Text>
              </Button>
              <Button
                type="secondary"
                onPress={confirmDisableMfa}
                disabled={loading}
              >
                <Text>{I18n.t("profile.mfa.disableConfirmation.disable")}</Text>
              </Button>
            </View>
          </View>
        </View>
      </Modal>
    </>
  );
}

const styles = StyleSheet.create({
  container: {
    padding: 16,
    alignItems: "center",
  },
  loader: {
    marginTop: 12,
  },
  statusText: {
    marginTop: 12,
    fontSize: 16,
    fontWeight: "bold",
    marginBottom: 16,
  },
  buttonContainer: {
    flexDirection: "row",
    gap: 10,
    marginBottom: 10,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    justifyContent: "center",
    alignItems: "center",
  },
  modalContainer: {
    backgroundColor: Colors.white,
    margin: 30,
    borderRadius: 10,
    borderWidth: 1,
    padding: 20,
    alignItems: "center",
    shadowColor: Colors.black,
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.25,
    shadowRadius: 4,
    elevation: 5,
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: "bold",
    marginBottom: 10,
  },
  modalMessage: {
    textAlign: "center",
    marginBottom: 20,
    lineHeight: 20,
  },
  modalButtons: {
    flexDirection: "row",
    gap: 10,
  },
});
